import React, { useState, useEffect } from 'react';
import {
  X,
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Calculator,
  Package,
  Save,
} from 'lucide-react';
import type {
  OrdenCompra,
  ItemOrdenCompra,
  SugerenciaOrden,
} from '../types/ordenes';
import type { Ingrediente, Proveedor } from '../types/ingredientes';
import { useProveedores } from '../hooks/useProveedores';

interface OrdenCompraFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (orden: Partial<OrdenCompra>) => void;
  ordenExistente?: OrdenCompra;
  modo: 'crear' | 'editar';
  sugerenciasIniciales?: SugerenciaOrden[];
}

interface FormData {
  proveedorId: string;
  observaciones: string;
  fechaEsperada: string;
  items: {
    ingredienteId: string;
    cantidad: number;
    precioUnitario: number;
    observaciones?: string;
  }[];
}

interface NuevoIngredienteData {
  nombre: string;
  descripcion: string;
  categoria: string;
  unidadMedidaId: string;
  stockMinimo: number;
  diasAlertaVencimiento: number;
  precioUnitario: number;
}

const OrdenCompraForm: React.FC<OrdenCompraFormProps> = ({
  isOpen,
  onClose,
  onSave,
  ordenExistente,
  modo,
  sugerenciasIniciales = [],
}) => {
  // Estados del formulario
  const [formData, setFormData] = useState<FormData>({
    proveedorId: '',
    observaciones: '',
    fechaEsperada: '',
    items: [],
  });

  // Estados para datos de apoyo
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [ingredientes, setIngredientes] = useState<Ingrediente[]>([]);
  const [sugerencias, setSugerencias] = useState<SugerenciaOrden[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSugerencias, setShowSugerencias] = useState(true);
  const [showNuevoIngrediente, setShowNuevoIngrediente] = useState(false);

  // Obtener proveedores reales de la API
  const { data: proveedoresData, isLoading: proveedoresLoading } =
    useProveedores();

  // Efecto para cargar proveedores reales desde la API
  useEffect(() => {
    if (proveedoresData?.data) {
      // Mapear los proveedores de la API al formato esperado
      const proveedoresMapeados = proveedoresData.data
        .filter((prov: any) => prov.activo) // Solo proveedores activos
        .map((prov: any) => ({
          id: prov.id.toString(),
          nombre: prov.nombre,
          contacto: '', // No está disponible en la API actual
          email: prov.email || '',
          telefono: prov.telefono || '',
          direccion: prov.direccion || '',
          activo: prov.activo,
        }));
      setProveedores(proveedoresMapeados);
    }
  }, [proveedoresData]);

  // Inicializar ingredientes mock (esto debería ser reemplazado eventualmente por una API)
  useEffect(() => {
    // Ingredientes mock
    setIngredientes([
      {
        id: '1',
        nombre: 'Pollo Entero',
        descripcion: 'Pollo fresco para preparaciones',
        categoria: 'Carnes',
        unidadMedidaId: '1',
        unidadMedida: {
          id: '1',
          nombre: 'Kilogramos',
          abreviacion: 'kg',
          tipo: 'peso',
          activa: true,
        },
        stockActual: 2.5,
        stockMinimo: 5.0,
        stockReservado: 0,
        diasAlertaVencimiento: 3,
        activo: true,
        fechaCreacion: new Date('2024-01-15'),
        fechaActualizacion: new Date(),
        proveedores: [
          {
            id: '1',
            ingredienteId: '1',
            proveedorId: '2',
            proveedor: {
              id: '2',
              nombre: 'Carnicería San Juan',
              contacto: 'María González',
              email: 'maria@carniceria.cl',
              telefono: '+56987654321',
              direccion: 'San Juan 456',
              activo: true,
            },
            precioUnitario: 2500,
            tiempoEntrega: 2,
            fechaUltimoPrecio: new Date(),
            activo: true,
          },
        ],
      },
      {
        id: '2',
        nombre: 'Arroz Grano Largo',
        descripcion: 'Arroz premium para guarniciones',
        categoria: 'Granos',
        unidadMedidaId: '1',
        unidadMedida: {
          id: '1',
          nombre: 'Kilogramos',
          abreviacion: 'kg',
          tipo: 'peso',
          activa: true,
        },
        stockActual: 15.0,
        stockMinimo: 10.0,
        stockReservado: 2.0,
        diasAlertaVencimiento: 30,
        activo: true,
        fechaCreacion: new Date('2024-02-01'),
        fechaActualizacion: new Date(),
        proveedores: [
          {
            id: '2',
            ingredienteId: '2',
            proveedorId: '1',
            proveedor: {
              id: '1',
              nombre: 'Distribuidora Central',
              contacto: 'Juan Pérez',
              email: 'juan@distribuidora.cl',
              telefono: '+56912345678',
              direccion: 'Av. Principal 123',
              activo: true,
            },
            precioUnitario: 1200,
            tiempoEntrega: 3,
            fechaUltimoPrecio: new Date(),
            activo: true,
          },
        ],
      },
      {
        id: '3',
        nombre: 'Aceite Vegetal',
        descripcion: 'Aceite para frituras y preparaciones',
        categoria: 'Aceites',
        unidadMedidaId: '2',
        unidadMedida: {
          id: '2',
          nombre: 'Litros',
          abreviacion: 'L',
          tipo: 'volumen',
          activa: true,
        },
        stockActual: 8.0,
        stockMinimo: 10.0,
        stockReservado: 1.0,
        diasAlertaVencimiento: 60,
        activo: true,
        fechaCreacion: new Date('2024-02-10'),
        fechaActualizacion: new Date(),
        proveedores: [
          {
            id: '3',
            ingredienteId: '3',
            proveedorId: '1',
            proveedor: {
              id: '1',
              nombre: 'Distribuidora Central',
              contacto: 'Juan Pérez',
              email: 'juan@distribuidora.cl',
              telefono: '+56912345678',
              direccion: 'Av. Principal 123',
              activo: true,
            },
            precioUnitario: 1800,
            tiempoEntrega: 2,
            fechaUltimoPrecio: new Date(),
            activo: true,
          },
        ],
      },
    ]);

    // Sugerencias automáticas basadas en stock
    const sugerenciasAutomaticas: SugerenciaOrden[] = [
      {
        ingredienteId: '1',
        ingrediente: {
          id: '1',
          nombre: 'Pollo Entero',
          stockActual: 2.5,
          stockMinimo: 5.0,
          unidadMedida: { abreviacion: 'kg' },
        },
        cantidadSugerida: 25,
        prioridad: 'alta',
        razon: 'Stock crítico - Por debajo del mínimo',
        diasParaAgotamiento: 2,
        proveedorSugerido: {
          id: '2',
          nombre: 'Carnicería San Juan',
          precioUnitario: 2500,
        },
      },
      {
        ingredienteId: '3',
        ingrediente: {
          id: '3',
          nombre: 'Aceite Vegetal',
          stockActual: 8.0,
          stockMinimo: 10.0,
          unidadMedida: { abreviacion: 'L' },
        },
        cantidadSugerida: 20,
        prioridad: 'media',
        razon: 'Stock por debajo del mínimo',
        diasParaAgotamiento: 7,
        proveedorSugerido: {
          id: '1',
          nombre: 'Distribuidora Central',
          precioUnitario: 1800,
        },
      },
    ];

    setSugerencias([...sugerenciasIniciales, ...sugerenciasAutomaticas]);
  }, [sugerenciasIniciales]);

  // Filtrar sugerencias basándose en ingredientes ya agregados y eliminar duplicados
  const sugerenciasUnicas = sugerencias.reduce(
    (acc: SugerenciaOrden[], current) => {
      const exists = acc.find(
        (item) => item.ingredienteId === current.ingredienteId
      );
      if (!exists) {
        acc.push(current);
      }
      return acc;
    },
    []
  );

  const sugerenciasFiltradas = sugerenciasUnicas.filter(
    (sugerencia) =>
      !formData.items.some(
        (item) => item.ingredienteId === sugerencia.ingredienteId
      )
  );

  // Inicializar formulario si es edición
  useEffect(() => {
    if (modo === 'editar' && ordenExistente) {
      const fechaEsperada = ordenExistente.fechaEsperada;
      const fechaEsperadaStr = fechaEsperada instanceof Date
        ? fechaEsperada.toISOString().split('T')[0]
        : typeof fechaEsperada === 'string'
          ? fechaEsperada.split('T')[0]
          : '';
      setFormData({
        proveedorId: ordenExistente.proveedorId || '',
        observaciones: ordenExistente.observaciones || '',
        fechaEsperada: fechaEsperadaStr,
        items: (ordenExistente.items ?? []).map((item) => ({
          ingredienteId: item.ingredienteId,
          cantidad: item.cantidad,
          precioUnitario: item.precioUnitario,
          observaciones: item.observaciones,
        })),
      });
    }
  }, [modo, ordenExistente]);

  const agregarItem = (
    ingredienteId?: string,
    cantidad?: number,
    precio?: number
  ) => {
    const nuevoItem = {
      ingredienteId: ingredienteId || '',
      cantidad: cantidad || 1,
      precioUnitario: precio || 0,
      observaciones: '',
    };

    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, nuevoItem],
    }));
  };

  const eliminarItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const actualizarItem = (index: number, campo: string, valor: any) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [campo]: valor } : item
      ),
    }));
  };

  const aplicarSugerencia = (sugerencia: SugerenciaOrden) => {
    // Verificar si el ingrediente ya está en la lista
    const existeItem = formData.items.some(
      (item) => item.ingredienteId === sugerencia.ingredienteId
    );

    if (!existeItem) {
      agregarItem(
        sugerencia.ingredienteId,
        sugerencia.cantidadSugerida,
        sugerencia.proveedorSugerido?.precioUnitario || 0
      );

      // Si hay proveedor sugerido y no se ha seleccionado uno, usarlo
      if (sugerencia.proveedorSugerido && !formData.proveedorId) {
        setFormData((prev) => ({
          ...prev,
          proveedorId: sugerencia.proveedorSugerido!.id,
        }));
      }

      // Mostrar feedback visual (opcional: podríamos agregar un toast notification aquí)
      console.log(
        `✅ Agregado: ${sugerencia.ingrediente.nombre} (${sugerencia.cantidadSugerida} ${sugerencia.ingrediente.unidadMedida.abreviacion})`
      );
    }
  };

  const calcularSubtotal = (cantidad: number, precio: number) => {
    return cantidad * precio;
  };

  const calcularTotal = () => {
    const subtotal = formData.items.reduce(
      (sum, item) => sum + calcularSubtotal(item.cantidad, item.precioUnitario),
      0
    );
    const impuestos = subtotal * 0.19; // IVA 19%
    return { subtotal, impuestos, total: subtotal + impuestos };
  };

  // Filtrar ingredientes por proveedor seleccionado
  const ingredientesFiltrados = React.useMemo(() => {
    if (!formData.proveedorId) {
      return ingredientes; // Si no hay proveedor seleccionado, mostrar todos
    }

    return ingredientes.filter((ingrediente) =>
      ingrediente.proveedores?.some(
        (proveedor) =>
          proveedor.proveedorId === formData.proveedorId && proveedor.activo
      )
    );
  }, [ingredientes, formData.proveedorId]);

  // Función para agregar un nuevo ingrediente al proveedor
  const agregarNuevoIngrediente = (nuevoIngrediente: NuevoIngredienteData) => {
    const id = `ing_${Date.now()}`;
    const ingredienteCompleto: Ingrediente = {
      id,
      nombre: nuevoIngrediente.nombre,
      descripcion: nuevoIngrediente.descripcion,
      categoria: nuevoIngrediente.categoria,
      unidadMedidaId: nuevoIngrediente.unidadMedidaId,
      unidadMedida: {
        id: nuevoIngrediente.unidadMedidaId,
        nombre:
          nuevoIngrediente.unidadMedidaId === '1'
            ? 'Kilogramos'
            : nuevoIngrediente.unidadMedidaId === '2'
              ? 'Litros'
              : 'Unidades',
        abreviacion:
          nuevoIngrediente.unidadMedidaId === '1'
            ? 'kg'
            : nuevoIngrediente.unidadMedidaId === '2'
              ? 'L'
              : 'un',
        tipo:
          nuevoIngrediente.unidadMedidaId === '1'
            ? 'peso'
            : nuevoIngrediente.unidadMedidaId === '2'
              ? 'volumen'
              : 'unidad',
        activa: true,
      },
      stockActual: 0,
      stockMinimo: nuevoIngrediente.stockMinimo,
      stockReservado: 0,
      diasAlertaVencimiento: nuevoIngrediente.diasAlertaVencimiento,
      activo: true,
      fechaCreacion: new Date(),
      fechaActualizacion: new Date(),
      proveedores: formData.proveedorId
        ? [
            {
              id: `prov_${Date.now()}`,
              ingredienteId: id,
              proveedorId: formData.proveedorId,
              proveedor: proveedores.find(
                (p) => p.id === formData.proveedorId
              )!,
              precioUnitario: nuevoIngrediente.precioUnitario,
              tiempoEntrega: 1,
              fechaUltimoPrecio: new Date(),
              activo: true,
            },
          ]
        : [],
    };

    // Agregar el nuevo ingrediente a la lista
    setIngredientes((prev) => [...prev, ingredienteCompleto]);

    // Cerrar el modal
    setShowNuevoIngrediente(false);

    // Agregar automáticamente a la orden si se desea
    return ingredienteCompleto;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    guardarOrden('enviada');
  };

  const handleGuardarBorrador = () => {
    guardarOrden('borrador');
  };

  const guardarOrden = (estado: 'borrador' | 'enviada') => {
    setLoading(true);

    // Simular envío
    setTimeout(() => {
      const { subtotal, impuestos, total } = calcularTotal();

      const ordenData: Partial<OrdenCompra> = {
        estado,
        proveedorId: formData.proveedorId,
        observaciones: formData.observaciones,
        fechaEsperada: formData.fechaEsperada
          ? new Date(formData.fechaEsperada)
          : undefined,
        subtotal,
        impuestos,
        total,
        items: formData.items.map((item, index) => ({
          id: `${Date.now()}_${index}`,
          ordenCompraId: '',
          ingredienteId: item.ingredienteId,
          ingrediente: ingredientes.find(
            (ing) => ing.id === item.ingredienteId
          )!,
          cantidad: item.cantidad,
          precioUnitario: item.precioUnitario,
          subtotal: calcularSubtotal(item.cantidad, item.precioUnitario),
          observaciones: item.observaciones,
        })) as ItemOrdenCompra[],
      };

      onSave(ordenData);
      setLoading(false);
      onClose();

      // Reset form
      setFormData({
        proveedorId: '',
        observaciones: '',
        fechaEsperada: '',
        items: [],
      });
    }, 1000);
  };

  const getPrioridadColor = (prioridad: string) => {
    const colores = {
      alta: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
      media:
        'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20',
      baja: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
    };
    return colores[prioridad as keyof typeof colores] || colores.baja;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(amount);
  };

  if (!isOpen) return null;

  const { subtotal, impuestos, total } = calcularTotal();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {modo === 'crear'
                ? 'Nueva Orden de Compra'
                : 'Editar Orden de Compra'}
            </h2>
            {modo === 'editar' && ordenExistente && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {ordenExistente.numero} - Estado:
                <span
                  className={`ml-1 font-medium ${
                    ordenExistente.estado === 'borrador'
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : ordenExistente.estado === 'enviada'
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-green-600 dark:text-green-400'
                  }`}
                >
                  {ordenExistente.estado === 'borrador' && '📝 Borrador'}
                  {ordenExistente.estado === 'enviada' && '📤 Enviada'}
                  {ordenExistente.estado === 'recibida' && '✅ Recibida'}
                  {ordenExistente.estado === 'cancelada' && '❌ Cancelada'}
                </span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Panel de sugerencias */}
          {showSugerencias && sugerenciasFiltradas.length > 0 && (
            <div className="w-80 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  Sugerencias Inteligentes
                  {sugerenciasFiltradas.length !== sugerenciasUnicas.length && (
                    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                      ({sugerenciasFiltradas.length} restantes)
                    </span>
                  )}
                </h3>
                <button
                  onClick={() => setShowSugerencias(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-3">
                {sugerenciasFiltradas.map((sugerencia) => (
                  <div
                    key={sugerencia.ingredienteId}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${getPrioridadColor(sugerencia.prioridad)}`}
                      >
                        {sugerencia.prioridad.toUpperCase()}
                      </span>
                      {sugerencia.diasParaAgotamiento &&
                        sugerencia.diasParaAgotamiento <= 3 && (
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        )}
                    </div>

                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                      {sugerencia.ingrediente.nombre}
                    </h4>

                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {sugerencia.razon}
                    </p>

                    <div className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                      <div>
                        Stock actual: {sugerencia.ingrediente.stockActual}{' '}
                        {sugerencia.ingrediente.unidadMedida.abreviacion}
                      </div>
                      <div>
                        Cantidad sugerida: {sugerencia.cantidadSugerida}{' '}
                        {sugerencia.ingrediente.unidadMedida.abreviacion}
                      </div>
                      {sugerencia.proveedorSugerido && (
                        <div>
                          Precio:{' '}
                          {formatCurrency(
                            sugerencia.proveedorSugerido.precioUnitario
                          )}
                          /{sugerencia.ingrediente.unidadMedida.abreviacion}
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => aplicarSugerencia(sugerencia)}
                      className="w-full px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors duration-200"
                    >
                      Agregar a Orden
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Panel de mensaje de éxito cuando no hay más sugerencias */}
          {showSugerencias &&
            sugerenciasUnicas.length > 0 &&
            sugerenciasFiltradas.length === 0 && (
              <div className="w-80 border-r border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    Sugerencias Inteligentes
                  </h3>
                  <button
                    onClick={() => setShowSugerencias(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
                  <p className="font-medium mb-1">¡Excelente!</p>
                  <p className="text-sm">
                    Todos los ingredientes sugeridos han sido agregados a la
                    orden.
                  </p>
                </div>
              </div>
            )}

          {/* Formulario principal */}
          <div className="flex-1 overflow-y-auto">
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Información básica */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Proveedor *
                  </label>
                  <select
                    required
                    value={formData.proveedorId}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        proveedorId: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="">
                      {proveedoresLoading
                        ? 'Cargando proveedores...'
                        : 'Seleccionar proveedor...'}
                    </option>
                    {!proveedoresLoading &&
                      proveedores.map((proveedor) => (
                        <option key={proveedor.id} value={proveedor.id}>
                          {proveedor.nombre}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fecha Esperada de Entrega
                  </label>
                  <input
                    type="date"
                    value={formData.fechaEsperada}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        fechaEsperada: e.target.value,
                      }))
                    }
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>

              {/* Items de la orden */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Items de la Orden
                  </h3>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => agregarItem()}
                      className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Agregar Item
                    </button>
                    {formData.proveedorId && (
                      <button
                        type="button"
                        onClick={() => setShowNuevoIngrediente(true)}
                        className="inline-flex items-center px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Nuevo Ingrediente
                      </button>
                    )}
                  </div>
                </div>

                {formData.items.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <div className="mb-2">
                      No hay items en la orden. Agrega items manualmente o usa
                      las sugerencias.
                    </div>
                    {formData.proveedorId && (
                      <div className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                        ℹ️ Los ingredientes mostrados corresponden a los
                        disponibles para el proveedor seleccionado
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.items.map((item, index) => {
                      const ingrediente = ingredientes.find(
                        (ing) => ing.id === item.ingredienteId
                      );
                      return (
                        <div
                          key={index}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Ingrediente *
                              </label>
                              <select
                                required
                                value={item.ingredienteId}
                                onChange={(e) =>
                                  actualizarItem(
                                    index,
                                    'ingredienteId',
                                    e.target.value
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                              >
                                <option value="">
                                  Seleccionar ingrediente...
                                </option>
                                {ingredientesFiltrados.map((ing) => (
                                  <option key={ing.id} value={ing.id}>
                                    {ing.nombre} ({ing.unidadMedida.abreviacion}
                                    )
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Cantidad *
                              </label>
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                required
                                value={item.cantidad}
                                onChange={(e) =>
                                  actualizarItem(
                                    index,
                                    'cantidad',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Precio Unitario *
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                required
                                value={item.precioUnitario}
                                onChange={(e) =>
                                  actualizarItem(
                                    index,
                                    'precioUnitario',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                              />
                            </div>

                            <div className="flex items-center space-x-2">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {formatCurrency(
                                  calcularSubtotal(
                                    item.cantidad,
                                    item.precioUnitario
                                  )
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => eliminarItem(index)}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>

                          {ingrediente && (
                            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                              Stock actual: {ingrediente.stockActual}{' '}
                              {ingrediente.unidadMedida.abreviacion} | Stock
                              mínimo: {ingrediente.stockMinimo}{' '}
                              {ingrediente.unidadMedida.abreviacion}
                              {ingrediente.stockActual <
                                ingrediente.stockMinimo && (
                                <span className="ml-2 text-red-600 dark:text-red-400 font-medium">
                                  ⚠️ Stock bajo
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Observaciones */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Observaciones
                </label>
                <textarea
                  rows={3}
                  value={formData.observaciones}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      observaciones: e.target.value,
                    }))
                  }
                  placeholder="Instrucciones especiales, condiciones de entrega, etc."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              {/* Resumen de totales */}
              {formData.items.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                    <Calculator className="h-5 w-5 mr-2" />
                    Resumen de la Orden
                  </h3>

                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        Subtotal:
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {formatCurrency(subtotal)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">
                        IVA (19%):
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {formatCurrency(impuestos)}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t border-gray-200 dark:border-gray-600 pt-2">
                      <span className="text-gray-900 dark:text-gray-100">
                        Total:
                      </span>
                      <span className="text-blue-600 dark:text-blue-400">
                        {formatCurrency(total)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Botones de acción */}
              <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  Cancelar
                </button>

                {/* Botón Guardar como Borrador */}
                {(modo === 'crear' ||
                  (modo === 'editar' &&
                    ordenExistente?.estado === 'borrador')) && (
                  <button
                    type="button"
                    onClick={handleGuardarBorrador}
                    disabled={
                      loading ||
                      formData.items.length === 0 ||
                      !formData.proveedorId
                    }
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:bg-gray-200 dark:disabled:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg transition-colors duration-200 flex items-center"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <svg
                          className="h-4 w-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a.997.997 0 01-.707.293H7a4 4 0 01-4-4V7a4 4 0 014-4z"
                          />
                        </svg>
                        {modo === 'crear'
                          ? 'Guardar Borrador'
                          : 'Mantener en Borrador'}
                      </>
                    )}
                  </button>
                )}

                <button
                  type="submit"
                  disabled={
                    loading ||
                    formData.items.length === 0 ||
                    !formData.proveedorId
                  }
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {modo === 'crear'
                        ? 'Enviar Orden'
                        : ordenExistente?.estado === 'borrador'
                          ? 'Enviar Orden'
                          : 'Actualizar Orden'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Modal para Nuevo Ingrediente */}
      {showNuevoIngrediente && (
        <NuevoIngredienteModal
          isOpen={showNuevoIngrediente}
          onClose={() => setShowNuevoIngrediente(false)}
          onSave={agregarNuevoIngrediente}
          proveedorNombre={
            proveedores.find((p) => p.id === formData.proveedorId)?.nombre || ''
          }
        />
      )}
    </div>
  );
};

// Componente del Modal para Nuevo Ingrediente
interface NuevoIngredienteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (ingrediente: NuevoIngredienteData) => void;
  proveedorNombre: string;
}

const NuevoIngredienteModal: React.FC<NuevoIngredienteModalProps> = ({
  isOpen,
  onClose,
  onSave,
  proveedorNombre,
}) => {
  const [formData, setFormData] = useState<NuevoIngredienteData>({
    nombre: '',
    descripcion: '',
    categoria: 'Otros',
    unidadMedidaId: '1',
    stockMinimo: 1,
    diasAlertaVencimiento: 7,
    precioUnitario: 0,
  });

  const categorias = [
    'Carnes',
    'Pescados y Mariscos',
    'Verduras',
    'Frutas',
    'Lácteos',
    'Granos',
    'Aceites',
    'Especias',
    'Bebidas',
    'Otros',
  ];

  const unidadesMedida = [
    { id: '1', nombre: 'Kilogramos', abreviacion: 'kg' },
    { id: '2', nombre: 'Litros', abreviacion: 'L' },
    { id: '3', nombre: 'Unidades', abreviacion: 'un' },
    { id: '4', nombre: 'Gramos', abreviacion: 'g' },
    { id: '5', nombre: 'Mililitros', abreviacion: 'ml' },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    setFormData({
      nombre: '',
      descripcion: '',
      categoria: 'Otros',
      unidadMedidaId: '1',
      stockMinimo: 1,
      diasAlertaVencimiento: 7,
      precioUnitario: 0,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
              <Package className="h-6 w-6 mr-2 text-green-600" />
              Nuevo Ingrediente
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Se vinculará automáticamente con:{' '}
              <span className="font-medium">{proveedorNombre}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-6 space-y-4 max-h-[70vh] overflow-y-auto"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nombre del Ingrediente *
              </label>
              <input
                type="text"
                required
                value={formData.nombre}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, nombre: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                placeholder="Ej: Pechuga de Pollo Premium"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Descripción
              </label>
              <textarea
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    descripcion: e.target.value,
                  }))
                }
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                placeholder="Descripción detallada del ingrediente..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Categoría *
              </label>
              <select
                required
                value={formData.categoria}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    categoria: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              >
                {categorias.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Unidad de Medida *
              </label>
              <select
                required
                value={formData.unidadMedidaId}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    unidadMedidaId: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              >
                {unidadesMedida.map((unidad) => (
                  <option key={unidad.id} value={unidad.id}>
                    {unidad.nombre} ({unidad.abreviacion})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Stock Mínimo *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                required
                value={formData.stockMinimo}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    stockMinimo: parseFloat(e.target.value) || 0,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Precio Unitario *
              </label>
              <input
                type="number"
                min="0"
                step="1"
                required
                value={formData.precioUnitario}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    precioUnitario: parseFloat(e.target.value) || 0,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Días de Alerta de Vencimiento
              </label>
              <input
                type="number"
                min="1"
                value={formData.diasAlertaVencimiento}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    diasAlertaVencimiento: parseInt(e.target.value) || 7,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Package className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
              <div className="text-sm">
                <p className="text-green-800 dark:text-green-200 font-medium">
                  Este ingrediente se vinculará automáticamente con:
                </p>
                <p className="text-green-700 dark:text-green-300 mt-1">
                  <span className="font-medium">{proveedorNombre}</span> con el
                  precio unitario especificado
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 flex items-center"
            >
              <Save className="h-4 w-4 mr-2" />
              Crear Ingrediente
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrdenCompraForm;
